import axios, {
  Axios,
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios'
import { Configuration } from './generated-code/configuration'
import {
  ProfileApi,
  GroupApi,
  ThreadApi,
  ContactApi,
  MessageApi,
  AuthenticationApi,
  Meta,
  Group,
  Contact,
  Login200Response,
  UserSettings,
} from './generated-code/api'
import lodash from 'lodash'
import { createHmac } from 'crypto'

// import {
//   getAxiosRequestParams,
//   isAxiosRequestConfig,
// } from '@sendhub/shared-utils'
// TODO: fix this import issue...

// TODO: move this to a utils library
function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}
// TODO: move this to a utils library
function escapeReplacement(string: string) {
  return string.replace(/\$/g, '$$$$')
}

export const GROUP_NUMBER_MERGEFIELD = '{group_num}' as const
export const DEFAULT_GROUP_NUMBERING_PATTERN =
  ` (${GROUP_NUMBER_MERGEFIELD})` as const
export const GROUP_NUMBERING_PATTERN_MERGEFIELD = '{group_num_str}' as const

/**
 * Returns DEFAULT_GROUP_NUMBERING_PATTERN if GROUP_NUMBER_MERGEFIELD not in groupNumStr
 */
export function ensureGroupNumberMergefieldIsIncluded(
  groupNumStr: string
): string {
  return groupNumStr && groupNumStr.includes(GROUP_NUMBER_MERGEFIELD)
    ? groupNumStr
    : DEFAULT_GROUP_NUMBERING_PATTERN
}
/**
 * Returns groupName with GROUP_NUMBERING_PATTERN_MERGEFIELD appended if missing
 */
export function ensureGroupNumberingPatternMergefieldIsIncluded(
  groupName: string
): string {
  return groupName && groupName.includes(GROUP_NUMBERING_PATTERN_MERGEFIELD)
    ? groupName
    : groupName + GROUP_NUMBERING_PATTERN_MERGEFIELD
}

function isAxiosRequestConfig(data: any): data is AxiosRequestConfig {
  if (!('method' in data)) throw new Error('method not in axiosRequestConfig')
  return 'method' in data
}
function getAxiosRequestParams(
  config: AxiosRequestConfig
): Record<string, string> {
  const url = new URL(config.url || '')
  const urlSearchParams = new URLSearchParams(url.search)
  const paramsFromUrl = Object.fromEntries(urlSearchParams.entries())
  return { ...paramsFromUrl, ...(config.params || {}) }
}

interface GenericListResponse {
  meta: Meta
  objects: any[]
}

function isListResponse(data: any): data is GenericListResponse {
  return (
    'meta' in data &&
    'next' in data.meta &&
    'objects' in data &&
    lodash.isArray(data.objects)
  )
}

export async function pageAll<T = any>(
  resp: AxiosResponse,
  axiosInstance: Axios = axios,
  data: T[] = []
): Promise<T[]> {
  if (!isListResponse(resp.data))
    throw new Error(`resp.data isn't a list response! ${resp.data}`)
  if (!isAxiosRequestConfig(resp.config))
    throw new Error(`resp.config isn't an AxiosRequestConfig! ${resp.config}`)

  data.push(...resp.data.objects)
  const isMoreData =
    typeof resp.data.meta.next === 'string' && resp.data.meta.next.length > 0
  if (!isMoreData) return data
  // Have to take the headers out - for some reason there's a weird invalid header "0" being added otherwise
  const newRequestConfig = { ...resp.config, headers: undefined }
  const params = getAxiosRequestParams(resp.config)
  const limit =
    params['limit'] === undefined ? undefined : parseInt(params['limit'])
  const offset =
    params['offset'] === undefined ? undefined : parseInt(params['offset'])

  newRequestConfig.params = {
    ...params,
    limit,
    offset: (offset || 0) + (limit || 0),
  }
  try {
    return await pageAll(
      await axiosInstance.request(newRequestConfig),
      axiosInstance,
      data
    )
  } catch (error) {
    console.error(error)
    throw error
  }
}

export type Credentials =
  | {
      username: string
      password: string
    }
  | {
      username: string
      apiKey: string
    }
// TODO: Implement accessToken...
//   | {
//       accessToken: string
//     }

export class SendhubApiClient {
  public api: {
    profile: ProfileApi
    group: GroupApi
    thread: ThreadApi
    contact: ContactApi
    message: MessageApi
    auth: AuthenticationApi
  }
  axios: AxiosInstance
  baseUrl: string
  credentials?: Credentials
  private _apiKey?: string
  constructor(credentials?: Credentials, baseUrl = 'https://api.sendhub.com') {
    this.axios = axios.create()
    this.baseUrl = baseUrl
    console.log(`[SendhubApiClient]: baseUrl:`, baseUrl)
    this.credentials = credentials
    const configuration = new Configuration(
      credentials
        ? {
            username: credentials.username,
            password:
              'apiKey' in credentials
                ? credentials.apiKey
                : credentials.password,
          }
        : {
            baseOptions: { withCredentials: true },
          }
    )
    this.axios.interceptors.request.use(
      (r) => r,
      (err) => {
        if (err instanceof AxiosError) {
          console.error(`Axios request error:`, err.toJSON())
          throw err
        } else {
          console.error(`Axios request error:`, err)
          throw err
        }
      }
    )
    this.axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err instanceof AxiosError) {
          console.error(`Axios error response:`, err.toJSON())
          throw err
        } else {
          console.error(`Axios response error:`, err)
          throw err
        }
      }
    )
    this.api = {
      profile: new ProfileApi(configuration, baseUrl, this.axios),
      group: new GroupApi(configuration, baseUrl, this.axios),
      thread: new ThreadApi(configuration, baseUrl, this.axios),
      contact: new ContactApi(configuration, baseUrl, this.axios),
      message: new MessageApi(configuration, baseUrl, this.axios),
      auth: new AuthenticationApi(configuration, baseUrl, this.axios),
    }
  }

  public async getContactByPhone(number: string): Promise<Contact | undefined> {
    // TODO: Add a clean number function
    const resp = await this.api.contact.listContacts(
      undefined,
      undefined,
      number
    )
    if (resp.data.objects && resp.data.objects.length > 1)
      console.warn(
        `More than one contact record returned for phone number ${number}`
      )
    const contact = [...(resp.data.objects || []), undefined][0]
    return contact
  }

  public async isContactInGroup(
    contactId: string,
    groupId: string
  ): Promise<boolean> {
    const contact = (await this.api.contact.getContact(contactId)).data
    const groupIds = (contact.groups || []).map((groupUri) => {
      const splits = groupUri.split('/')
      return splits[splits.length - 1]
    })
    return groupIds.includes(groupId)
  }

  public async isIntegrationsAllowed() {
    const profileResp = await this.api.profile.getProfile()

    console.log(`[ApiClient.isIntegrationsAllowed()]: profileResp:`, {
      status: profileResp.status,
      data: profileResp.data,
    })
    const enterpriseResp = await this.api.profile.getEnterprise(
      profileResp.data.enterprise_id
    )
    console.log(`[ApiClient.isIntegrationsAllowed()]: enterpriseResp:`, {
      status: enterpriseResp.status,
      data: enterpriseResp.data,
    })
    const enterpriseSettings = JSON.parse(enterpriseResp.data.settingsJson)
    console.log(
      `[ApiClient.isIntegrationsAllowed()]: enterpriseSettings:`,
      enterpriseSettings
    )
    return !!enterpriseSettings?.integrationsAllowed
  }

  public async getMaxSmsRecipients(): Promise<number> {
    const maxSmsRecipients = (await this.api.profile.getSettings()).data
      ?.plan_usage?.current_plan?.max_sms_recipients
    // A missing limit or -1 (SendHub's "unlimited") must be treated as
    // unbounded - otherwise every existing group looks "full" and
    // newSizedGroup() below creates a brand-new group for every contact.
    if (maxSmsRecipients === undefined || maxSmsRecipients < 0)
      return Number.MAX_SAFE_INTEGER
    // 0 is never a configured limit (products allow 1-5000); SendHub reports
    // it when the real limit is unknown (legacy billing, billing errors).
    // The real limit may be low, so it isn't safe to assume unlimited here.
    if (maxSmsRecipients === 0)
      console.warn('max_sms_recipients is 0 - SMS recipient limit is unknown')
    return maxSmsRecipients
  }

  public static pageAll = pageAll

  /**
   * Creates a numbered group if needed based on the max recipient limit
   * Returns an existing group if forContact is specified and already in a matching group
   */
  public async newSizedGroup(
    groupName: string,
    groupNumStr?: string,
    ignoreMaxRecipLimit = false,
    forContact?: Contact
  ): Promise<Group> {
    const matchingGroups = await this.getMatchingGroups(groupName, groupNumStr)
    console.log('forContact:', forContact)
    // If the contact is already in a matching group, return it
    if (forContact && forContact?.id) {
      const groupsContactIsIn: Group[] = []
      const promises: Promise<void>[] = []

      for (const group of matchingGroups) {
        if (group.id)
          promises.push(
            this.isContactInGroup(forContact.id!, group.id).then((i) => {
              if (i) groupsContactIsIn.push(group)
            })
          )
      }
      await Promise.all(promises)
      if (groupsContactIsIn.length > 0) return groupsContactIsIn[0]
    }

    // Return the first matchingGroup that has room (or the first one if ignoreMaxRecipLimit=True)
    if (ignoreMaxRecipLimit) return matchingGroups[0]
    const maxSmsRecipients = await this.getMaxSmsRecipients()
    console.log('maxSmsRecipients:', maxSmsRecipients)
    const groupsWithRoom = matchingGroups.filter(
      (group) => (group.size || 0) < maxSmsRecipients
    )

    console.log('groupsWithRoom:', groupsWithRoom)
    if (groupsWithRoom.length > 0) return groupsWithRoom[0]

    // If no matching_groups have room, create a group

    // +1 the highest group number, or default to 0 if there aren't any matchingGroups
    const newGroupNumber =
      Math.max(...matchingGroups.map((g) => g.groupNum), -1) + 1

    const groupNumString =
      newGroupNumber === 0
        ? ''
        : ensureGroupNumberMergefieldIsIncluded(groupNumStr || '').replace(
            GROUP_NUMBER_MERGEFIELD,
            `${newGroupNumber}`
          )
    const newGroupName = ensureGroupNumberingPatternMergefieldIsIncluded(
      groupName
    ).replace(GROUP_NUMBERING_PATTERN_MERGEFIELD, `${groupNumString}`)

    console.log(`Creating group ${newGroupName}...`)
    const newGroup = (
      await this.api.group.createGroup({
        name: newGroupName,
      })
    ).data
    return newGroup as unknown as Group // TS Hack
  }

  /**
   * Returns a compiled regex to match a provided group name with or without a group number
   * @param {string} groupName The name of the group to match, optionally containing '{group_num_str}'
   * @param {string} groupNumStr A string containing '{group_num}' to determine how/where to insert a group's number
   */
  private static compileGroupRegex(
    groupName: string,
    groupNumStr?: string
  ): RegExp {
    // Make sure '{group_num}' is in group_num_str
    const groupNumString = ensureGroupNumberMergefieldIsIncluded(
      `${groupNumStr}`
    )
    // Make sure '{group_num_str}' is in group_name
    const groupNameTemplate =
      ensureGroupNumberingPatternMergefieldIsIncluded(groupName)

    //
    // Prepare regex patterns
    //
    // Default would be: '(\ (\d+))?'
    const groupNumStrPattern = `(?:${groupNumString
      .split(GROUP_NUMBER_MERGEFIELD)
      .map(escapeRegExp)
      .join('(\\d+)')})?`

    // Default would be: '^My awesome group name(\ (\d+))?$'
    const groupNameTemplatePattern = `^${groupNameTemplate
      .split(GROUP_NUMBERING_PATTERN_MERGEFIELD)
      .map(escapeRegExp)
      .join(groupNumStrPattern)}$`

    return new RegExp(groupNameTemplatePattern)
  }
  /**
   * Gets a list of existing groups based on a provided groupName (and optional group_num formatting)
   * @param {string} groupName The name of the group to match, optionally containing '{group_num_str}'
   * @param {string} groupNumStr A string containing '{group_num}' to determine how/where to insert a group's number
   */
  public async getMatchingGroups(
    groupName: string,
    groupNumStr?: string
  ): Promise<(Group & { groupNum: number })[]> {
    const groupNameTemplateRegex = SendhubApiClient.compileGroupRegex(
      groupName,
      groupNumStr
    )

    const initialGroupsResp = await this.api.group.listGroups(1000, undefined, {
      params: { ordering: '-date_created' },
    })
    const profile = await this.api.profile.getProfile()
    const allGroups = await pageAll<Group>(initialGroupsResp, this.axios)
    const matchingGroups: Record<number, Group> = {}
    allGroups.forEach((group) => {
      const match = `${group.name}`.match(groupNameTemplateRegex)
      if (match === null) return
      matchingGroups[parseInt(match[1]) || 0] = group
    })

    return Object.keys(matchingGroups)
      .sort()
      .map((sortedKey) => ({
        ...matchingGroups[parseInt(sortedKey)],
        groupNum: parseInt(sortedKey),
      }))
  }
  public static formatScheduledAt(
    originalDate: Date,
    useTodaysDate = false
  ): string {
    const clonedDate = new Date(originalDate.toJSON())
    if (useTodaysDate) {
      const now = new Date()
      clonedDate.setUTCFullYear(now.getUTCFullYear())
      clonedDate.setUTCMonth(now.getUTCMonth())
      clonedDate.setUTCDate(now.getUTCDate())
      if (clonedDate <= now) clonedDate.setUTCDate(now.getUTCDate() + 1)
    }
    return clonedDate.toJSON().split('.')[0] + '+0000'
  }

  /**
   * managerLogin
   * Logs you into another user's account (user must be in the same Enterprise account). Requires the Admin role.
   */
  public async managerLogin(
    userId: string | bigint
  ): Promise<SendhubApiClient> {
    const tempSession = axios.create({
      baseURL: this.baseUrl,
      withCredentials: true,
    })
    const suResp: AxiosResponse<Login200Response, any> = await tempSession.post(
      `/v1/auth/su/${userId}`,
      {
        authenticationType: 'manager',
      },
      {
        auth:
          this?.credentials?.username === undefined
            ? undefined
            : {
                username: this.credentials.username,
                password:
                  'apiKey' in this.credentials
                    ? this.credentials.apiKey
                    : this.credentials.password,
              },
      }
    )
    if (!('username' in suResp.data))
      throw new Error(`su response doesn't have 'username' field!`)

    const cookie = (suResp?.headers?.['set-cookie'] || [])
      .map((c) => c.split(';')[0])
      .join('; ')

    const settingsResp: AxiosResponse<UserSettings, any> =
      await tempSession.get('/v1/settings', { headers: { Cookie: cookie } })
    if (suResp.data.username === undefined)
      throw new Error('suResp.data.username is undefined!')
    if (settingsResp.data.api_key === undefined)
      throw new Error('settingsResp.data.api_key is undefined!')

    return new SendhubApiClient({
      username: suResp.data.username,
      apiKey: settingsResp.data.api_key,
    })
  }

  public static simpleCleanNumber(dirtyNumber: string): string | undefined {
    const cleanedNumber = `+1${dirtyNumber
      .replaceAll(/[^0-9]/g, '')
      .replace(/^1+/, '')}`
    if (cleanedNumber.length !== 12) return undefined
    return cleanedNumber
  }

  public get apiKey(): Promise<string> {
    return this.fetchApiKey()
  }

  private async fetchApiKey(): Promise<string> {
    if (this._apiKey) return this._apiKey

    if (this.credentials && 'apiKey' in this.credentials) {
      const key = this.credentials.apiKey
      this._apiKey = key
      return key
    }

    const settings = (await this.api.profile.getSettings()).data
    if (settings.api_key === undefined) throw new Error(`apiKey is undefined`)
    const key = settings.api_key
    this._apiKey = key
    return key
  }

  public async generateWebhookSignature(
    url: string,
    webhookPayload: string
  ): Promise<string> {
    const apiKey = await this.apiKey

    return SendhubApiClient.generateWebhookSignature(
      url,
      webhookPayload,
      apiKey
    )
  }
  public static generateWebhookSignature(
    url: string,
    webhookPayload: string,
    apiKey: string
  ): string {
    const hash = createHmac('sha1', apiKey)
      .update(`${url}${webhookPayload}`)
      .digest('base64')
    return hash
  }
  public static verifyWebhook(
    url: string,
    webhookPayload: string,
    apiKey: string,
    expectedHash: string
  ): boolean {
    const hash = SendhubApiClient.generateWebhookSignature(
      url,
      webhookPayload,
      apiKey
    )
    return hash === expectedHash
  }
}
